import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Zap, Gavel, MapPin, Clock, DollarSign, ArrowRight, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/**
 * LiveJobsFeed — the Royal pro's live marketplace.
 *
 * Shows EVERY unclaimed job in the pro's category + city, auto-refreshing
 * every 15 s.  Fixed-price jobs get a one-tap ⚡ Claim button (atomic
 * backend assignment — first pro to tap wins).  Negotiable jobs route
 * to the existing bidding flow in NegotiableBidsInbox.
 */
export default function LiveJobsFeed({ provider }) {
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [claiming, setClaiming] = useState(null);
    const [lastRefresh, setLastRefresh] = useState(null);
    const pollRef = useRef(null);

    const refresh = async () => {
        try {
            const { data } = await api.get("/providers/me/available-jobs");
            // Only fixed-price here — negotiable bids live in NegotiableBidsInbox.
            const fixed = (data || []).filter(
                (j) => !j.is_negotiable && j.status === "pending"
            );
            setJobs(fixed);
            setLastRefresh(new Date());
        } catch {
            // 403 (not verified yet) or network — leave list empty.
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!provider?.user_id) return;
        refresh();
        pollRef.current = setInterval(refresh, 15000);
        return () => clearInterval(pollRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [provider?.user_id]);

    const claim = async (job) => {
        setClaiming(job.id);
        try {
            await api.post(`/bookings/${job.id}/claim`);
            toast.success(`Royal claim secured · ${job.service_name}`);
            setJobs((prev) => prev.filter((j) => j.id !== job.id));
            setTimeout(() => navigate(`/bookings/${job.id}`), 400);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            refresh();
        } finally {
            setClaiming(null);
        }
    };

    const relativeTime = useMemo(
        () => (iso) => {
            if (!iso) return "";
            const diff = (Date.now() - new Date(iso).getTime()) / 1000;
            if (diff < 60) return `${Math.round(diff)}s ago`;
            if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
            return `${Math.round(diff / 3600)}h ago`;
        },
        []
    );

    if (loading) {
        return (
            <div className="bg-card border border-white/10 p-7 rounded-sm" data-testid="live-jobs-loading">
                <div className="overline mb-3">Live Jobs</div>
                <div className="text-sm text-muted-foreground">Loading…</div>
            </div>
        );
    }

    if (jobs.length === 0) {
        return (
            <div className="bg-card border border-white/10 p-7 rounded-sm" data-testid="live-jobs-empty">
                <div className="flex items-center gap-2 mb-3">
                    <Zap size={14} className="gold-text" strokeWidth={1.5} />
                    <div className="overline">Live Jobs · 0 right now</div>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                    No open jobs in your category + city. We'll push the next one
                    the moment it drops — keep this tab open or enable Royal
                    Push notifications.
                </p>
                <div className="mt-3 text-[10px] text-muted-foreground/70 uppercase tracking-[0.2em]">
                    Auto-refresh · every 15s · last {lastRefresh ? relativeTime(lastRefresh.toISOString()) : "never"}
                </div>
            </div>
        );
    }

    return (
        <div
            className="bg-card border border-primary/40 p-7 lg:p-9 rounded-sm relative overflow-hidden"
            data-testid="live-jobs-feed"
        >
            <div className="absolute -top-24 -right-20 w-72 h-72 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
            <div className="relative">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                            </span>
                            <div className="overline" data-testid="live-jobs-count">
                                Live Jobs · {jobs.length} open · {provider?.locations?.[0] || "your area"}
                            </div>
                        </div>
                        <h3 className="font-serif text-2xl sm:text-3xl">
                            Grab a <span className="italic gold-grad">Royal job.</span>
                        </h3>
                    </div>
                    <button
                        onClick={refresh}
                        className="text-muted-foreground hover:text-foreground p-2 rounded-sm transition-colors"
                        aria-label="Refresh jobs"
                        data-testid="live-jobs-refresh"
                    >
                        <RefreshCw size={16} strokeWidth={1.5} />
                    </button>
                </div>
                <ul className="space-y-3">
                    {jobs.map((j) => (
                        <li
                            key={j.id}
                            className="flex items-start sm:items-center gap-4 p-4 bg-background/60 border border-white/10 rounded-sm hover:border-primary/40 transition-colors flex-col sm:flex-row"
                            data-testid={`live-job-${j.id}`}
                        >
                            <div className="flex-1 min-w-0 w-full">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-medium text-foreground truncate">
                                        {j.service_name}
                                    </span>
                                    <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-400">
                                        {relativeTime(j.created_at)}
                                    </span>
                                </div>
                                <div className="mt-1 flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
                                    <span className="flex items-center gap-1">
                                        <MapPin size={10} strokeWidth={1.5} /> {j.city || "—"}
                                    </span>
                                    {j.scheduled_at && (
                                        <span className="flex items-center gap-1">
                                            <Clock size={10} strokeWidth={1.5} />{" "}
                                            {new Date(j.scheduled_at).toLocaleString([], {
                                                month: "short",
                                                day: "numeric",
                                                hour: "numeric",
                                                minute: "2-digit",
                                            })}
                                        </span>
                                    )}
                                    <span className="flex items-center gap-1 gold-text">
                                        <DollarSign size={10} strokeWidth={1.5} />
                                        {Math.round(j.total_amount || 0).toLocaleString()} JMD
                                    </span>
                                </div>
                            </div>
                            <Button
                                size="sm"
                                onClick={() => claim(j)}
                                disabled={claiming === j.id}
                                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 px-4 w-full sm:w-auto"
                                data-testid={`claim-job-${j.id}`}
                            >
                                {claiming === j.id ? "Claiming…" : (
                                    <>
                                        <Zap size={12} strokeWidth={2} className="mr-1" />
                                        Grab it
                                        <ArrowRight size={12} className="ml-1" strokeWidth={1.5} />
                                    </>
                                )}
                            </Button>
                        </li>
                    ))}
                </ul>
                <div className="mt-4 text-[10px] text-muted-foreground/70 uppercase tracking-[0.2em] flex items-center gap-2">
                    <Gavel size={10} strokeWidth={1.5} />
                    Auto-refresh every 15s · First Royal pro to grab wins
                </div>
            </div>
        </div>
    );
}
